import { Badge, Button } from "react-bootstrap";
import { dateFormatter, dateTimeFormatter } from "../../../../../global";
import {
  faCalendarPlus,
  faDog,
  faKey,
  faSquareParking,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import Card from "../../../../../components/Cards/Basic";
import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "../../../../../config/axios";
import { useTranslation } from "react-i18next";
import history from "../../../../../global/history";
import {
  showSuccessToast,
  showValidationErrorToast,
} from "../../../../../components/Toasts";
import ConfirmDeleteDialog from "../../../../../components/Modal/ConfirmDelete";
import CategoryIcon from "../../../../../components/CategoryIcon";
import EllipsisDropdown, {
  EllipsisDropdownItem,
  EllipsisDropdownSection,
} from "../../../../../components/EllipsisDropdown";
import useModal from "src/components/Modal/hooks/useModal";
import CreateBookingOfferDialog from "src/pages/Dashboard/Bookings/StayDetailsPage/CreateBookingQuoteDialog";
import { navigateTo } from "src/global/navigationHelper";
import OnlineReservationDeclineDialog from "src/pages/Dashboard/OnlineReservations/OnlineReservationDeclineDialog";
import OnlineReservationCheckinDialog from "src/pages/Dashboard/Bookings/StayDetailsPage/OnlineReservationCheckinDialog";
import { DashboardContext } from "src/routes/DashboardRoutes";
import { useLocation } from "react-router-dom";
import SplitBookingDialog from "src/pages/Dashboard/Bookings/StayDetailsPage/SplitBookingDialog";
import moment from "moment";

const BookingSection = ({ stay, getStay, extend }) => {
  const [bookings, setBookings] = useState([]);
  const [selectedDeleteBooking, setDeleteBooking] = useState();
  const [selectedSplitBooking, setSelectedSplitBooking] = useState();
  const { t } = useTranslation("dashboard");
  const [selBookingOffer, setSelBookingOffer] = useState();
  const [ageGroups, setAgeGroups] = useState(null);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const { isShow: showCodeRedeemDialog, toggle: toggleRedeemDialog } =
    useModal();
  const { settings } = useContext(DashboardContext);
  const location = useLocation();

  useEffect(() => {
    getBookings();
    loadAgeGroups();
  }, []);

  useEffect(() => {
    const openBookingOfferId = location.state?.openBookingOfferId;

    if (!openBookingOfferId) {
      return;
    }

    const booking = bookings.find((b) => b.id === openBookingOfferId);
    if (!booking) {
      return;
    }

    setSelBookingOffer(booking);

    const nextState = { ...location.state };
    delete nextState.openBookingOfferId;
    history.replace(location.pathname, nextState);
  }, [bookings, location.pathname, location.state, selBookingOffer]);

  function loadAgeGroups() {
    axios
      .get("/api/v1/age_groups.json")
      .then(({ data }) => {
        setAgeGroups(data.entries);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function getBookings() {
    axios
      .get(`/api/v1/bookings.json?stay_id=${stay.id}&order=booked_from_asc`)
      .then(function ({ data }) {
        setBookings(data.entries);
      });
  }

  function handleEditBooking(booking) {
    history.push(`/dashboard/stays/${stay.id}/bookings/${booking.id}/edit`);
  }

  function handleSplitBookingSuccess() {
    setSelectedSplitBooking(null);
    getStay();
    getBookings();
    showSuccessToast();
  }

  function handleDeclineSuccess() {
    getStay();
    getBookings();
    showSuccessToast();
    setShowDeclineDialog(false);
  }

  function handleRedeemSuccess() {
    getStay();
    getBookings();
    toggleRedeemDialog();
  }

  function handleQuoteCreated(data) {
    navigateTo(`/dashboard/stays/${stay.id}/invoices/${data.id}`);
  }

  function canSplitBooking(booking) {
    if (!booking.booked_from || !booking.booked_until) {
      return false;
    }

    const bookedFrom = moment(booking.booked_from).startOf("day");
    const bookedUntil = moment(booking.booked_until).startOf("day");

    return bookedUntil.diff(bookedFrom, "days") > 1;
  }

  const deleteBooking = () => {
    axios
      .delete(`/api/v1/bookings/${selectedDeleteBooking.id}.json`)
      .then(function () {
        getStay();
        getBookings();
        setDeleteBooking(null);
      })
      .catch(function (error) {
        showValidationErrorToast(error);
      });
  };

  const accept = async () => {
    try {
      await axios.post(`/api/v1/stays/${stay.id}/accept_reservation.json`);
      getStay();
      getBookings();
      showSuccessToast();
    } catch (error) {
      showValidationErrorToast(error);
    }
  };

  const decline = async () => {
    setShowDeclineDialog(true);
  };

  return (
    <>
      <Card
        title={"Buchungen"}
        footer={
          <>
            {!["checkout", "cancelled"].includes(stay.state) && (
                <Button className="mr-2" variant="secondary" onClick={extend}>
                  Buchung hinzufügen
                </Button>
              )}
          </>
        }
      >
        {(stay.reservation_code || ["requested", "reserved", "cancelled"].includes(stay.state)) && (
          <div className={"mb-3"}>
            <h2>Reservierung</h2>
            {stay.state === "reserved" && <Badge variant="info" className="mb-2">Reserviert</Badge>}
            <div className={"mb-2"}>
              {stay.reservation_code && (
                <div className="font-weight-bolder text-black">
                  <FontAwesomeIcon
                    icon={faKey}
                    height={24}
                    className={"mr-2"}
                  />
                  {t("online_reservation_details.reservation_code")}:
                  <span className="pl-2 font-weight-bold text-black-50">
                    {stay.reservation_code}
                  </span>
                </div>
              )}
            </div>
            {(stay.state === "requested" ||
              stay.state === "cancelled") && (
              <Button variant="primary" onClick={accept} className={"mr-2"}>
                {t("online_reservation_details.actions.accept")}
              </Button>
            )}

            {stay.state === "reserved" && (
              <Button onClick={toggleRedeemDialog} className={"mr-2"}>
                {t("online_reservation_details.actions.redeem_code")}
              </Button>
            )}

            {(stay.state === "requested" ||
              stay.state === "reserved") && (
              <Button variant="secondary" onClick={decline}>
                {t("online_reservation_details.actions.decline")}
              </Button>
            )}
          </div>
        )}
        <div className="ui-table-container">
          <table cellSpacing="0">
            <thead>
              <tr>
                {settings?.business?.parking_slots_enabled && (
                  <th>
                    <div className={"text-nowrap"}>
                      <FontAwesomeIcon
                        icon={faSquareParking}
                        height={24}
                        className={"mr-2"}
                      />
                      Stellplatz
                    </div>
                  </th>
                )}
                <th>
                  <FontAwesomeIcon
                    icon={faCalendarPlus}
                    height={24}
                    className={"mr-2"}
                  />
                  Zeitraum
                </th>
                <th>Anzahl Gäste</th>
                <th>
                  <div className={"text-nowrap"}>
                    <FontAwesomeIcon
                      icon={faDog}
                      height={24}
                      className={"mr-2"}
                    />
                    Anzahl Hunde
                  </div>
                </th>
                <th>Status</th>
                <th>Erstellt am</th>
                <th>Optionen</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr key={booking.id}>
                  {settings?.business?.parking_slots_enabled && (
                    <td>
                      <div className="d-flex align-items-center">
                        {booking?.parking_slot?.category && (
                          <CategoryIcon
                            category={booking.parking_slot.category}
                            size={24}
                          />
                        )}
                        <span className="ml-2">
                          {booking?.parking_slot?.name}
                        </span>
                      </div>
                      {booking?.service_type === "wc_wash" && (
                        <Badge pill variant="primary" text="light">
                          WC-Wash
                        </Badge>
                      )}
                    </td>
                  )}
                  <td>
                    <div>
                      <div>
                        {dateFormatter(booking.booked_from)} -{" "}
                        {dateFormatter(booking.booked_until)}
                        {booking.warning_codes?.length > 0 && (
                          <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className="ml-2 text-warning"
                            title={booking.warning_codes
                              .map((warningCode) =>
                                t(
                                  `booking_details.booking_section.warnings.${warningCode}`
                                )
                              )
                              .join("\n")}
                          />
                        )}
                      </div>
                      <small>
                        {t("booking_details.booking_section.nights", {
                          count: booking.nights,
                        })}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div className={"d-flex"}>
                      {ageGroups &&
                        ageGroups.map((ageGroup) => (
                          <div key={ageGroup.id} className={"mr-3 text-center"}>
                            <div>
                              {ageGroup.from}-{ageGroup.to}
                            </div>
                            {booking.booking_age_groups.find(
                              (bookingAgeGroup) =>
                                bookingAgeGroup.age_group_id === ageGroup.id
                            )?.count ?? 0}
                          </div>
                        ))}
                    </div>
                  </td>
                  <td>
                    <span className="pl-2">{booking.amount_dogs ?? "-"}</span>
                  </td>
                  <td>
                    <Badge
                      pill
                      variant={
                        booking.state === "draft" ? "secondary" : "primary"
                      }
                      text="light"
                    >
                      {t(`booking.statuses.${booking.state}`)}
                    </Badge>
                    {booking.reservation_request_expires_at && (
                      <Badge pill variant={"warning"} text="light">
                        Fällig am{" "}
                        {dateFormatter(booking.reservation_request_expires_at)}
                      </Badge>
                    )}
                  </td>
                  <td>{dateTimeFormatter(booking.created_at)}</td>
                  <td>
                    <EllipsisDropdown className={"m-2"}>
                      <EllipsisDropdownSection>
                        <EllipsisDropdownItem
                          onClick={() => handleEditBooking(booking)}
                        >
                          Bearbeiten
                        </EllipsisDropdownItem>

                        {settings?.business?.parking_slots_enabled && (
                          <EllipsisDropdownItem
                            onClick={() => setSelectedSplitBooking(booking)}
                            disabled={!canSplitBooking(booking)}
                          >
                            {t(
                              "booking_details.booking_section.split_dialog.menu_item"
                            )}
                          </EllipsisDropdownItem>
                        )}

                        <EllipsisDropdownItem
                          onClick={() => setSelBookingOffer(booking)}
                        >
                          Angebot erstellen
                        </EllipsisDropdownItem>
                      </EllipsisDropdownSection>
                      <EllipsisDropdownSection>
                        <EllipsisDropdownItem
                          onClick={() => {
                            setDeleteBooking(booking);
                          }}
                        >
                          Löschen
                        </EllipsisDropdownItem>
                      </EllipsisDropdownSection>
                    </EllipsisDropdown>
                  </td>
                </tr>
              ))}

              {bookings.length === 0 && (
                <tr>
                  <td colSpan={6}>{t("common:components.table.noEntries")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedDeleteBooking && (
        <ConfirmDeleteDialog
          show={!!selectedDeleteBooking}
          handleClose={() => {
            setDeleteBooking(null);
          }}
          handleDelete={deleteBooking}
        />
      )}

      {selectedSplitBooking && (
        <SplitBookingDialog
          booking={selectedSplitBooking}
          stayId={stay.id}
          handleClose={() => setSelectedSplitBooking(null)}
          handleSubmit={handleSplitBookingSuccess}
        />
      )}

      {selBookingOffer && (
        <CreateBookingOfferDialog
          booking={selBookingOffer}
          handleSubmit={handleQuoteCreated}
          handleClose={() => setSelBookingOffer(null)}
        />
      )}

      {showDeclineDialog && (
        <OnlineReservationDeclineDialog
          stayId={stay.id}
          handleClose={() => {
            setShowDeclineDialog(false);
          }}
          handleSubmit={handleDeclineSuccess}
        />
      )}

      {showCodeRedeemDialog && (
        <OnlineReservationCheckinDialog
          stayId={stay.id}
          handleClose={() => {
            toggleRedeemDialog();
          }}
          handleSubmit={handleRedeemSuccess}
        />
      )}
    </>
  );
};

export default BookingSection;
